import EmptyState from 'components/EmptyState/EmptyState';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WeatherMapResizableLayout from 'features/Weather/features/WeatherMaps/components/WeatherMapResizableLayout/WeatherMapResizableLayout';
import useLocation from 'hooks/useLocation';
import useWindow from 'hooks/useWindow';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';
import {
  WeatherMapStaleDataNotice,
  WeatherMapUpdatingStatus,
} from '../../components/WeatherMapDataStatus/WeatherMapDataStatus';
import './assets/severeWeatherAlerts.scss';
import SevereWeatherAlertMapContent from './components/SevereWeatherAlertMapContent/SevereWeatherAlertMapContent';
import SevereWeatherAlertPanel from './components/SevereWeatherAlertPanel/SevereWeatherAlertPanel';
import {
  SEVERE_WEATHER_ALERTS_PAGE_LAYOUT,
  SEVERE_WEATHER_ALERTS_QUERY_OPTIONS,
  SEVERE_WEATHER_ALERTS_REFRESH_INTERVAL_MINUTES,
  SEVERE_WEATHER_ALERTS_ROUTE,
} from './config/severeWeatherAlerts.config';
import useSevereWeatherAlertSelection from './hooks/useSevereWeatherAlertSelection';

const SevereWeatherAlerts = () => {
  const { locations } = useLocation();
  const { width } = useWindow();
  const {
    data: alerts = [],
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = useGetAllActiveAlertsQuery(undefined, SEVERE_WEATHER_ALERTS_QUERY_OPTIONS);
  const {
    alertAreas,
    selectedArea,
    selectedAlert,
    selectedAlertId,
    selectArea,
    selectAreaById,
    selectAlert,
  } = useSevereWeatherAlertSelection(alerts);
  const isDesktop = width >= 992;

  if (isLoading) {
    return <WidgetLoadingAnimation />;
  }

  if (isError && !alerts.length) {
    return <ErrorMessage message={getApiErrorMessage(error)} onRetry={refetch} />;
  }

  if (!alertAreas.allAlertAreas.length) {
    return (
      <EmptyState
        title="Severe Weather Warnings"
        message={
          alerts.length
            ? 'Active alerts are available, but none include a map-ready boundary.'
            : 'No active severe weather alerts are available right now.'
        }
      />
    );
  }

  return (
    <section
      aria-label="Severe weather warnings"
      className={`severe-weather-alerts severe-weather-alerts-page d-flex w-100 flex-column gap-2${
        isDesktop ? ' h-100 min-h-0' : ''
      }`}
    >
      <WidgetTimeUpdate
        realtime
        route={SEVERE_WEATHER_ALERTS_ROUTE}
        updateIntervalBasis={SEVERE_WEATHER_ALERTS_REFRESH_INTERVAL_MINUTES}
      />

      {isError && (
        <WeatherMapStaleDataNotice message="The latest update failed. Displaying the most recently available alerts." />
      )}

      <WeatherMapResizableLayout
        {...SEVERE_WEATHER_ALERTS_PAGE_LAYOUT}
        isResizable={isDesktop}
        mapAriaLabel="Severe weather alerts map"
        panelAriaLabel="Severe weather alert information"
        resizeHandleAriaLabel="Resize severe weather map and information"
        containerClassName={isDesktop ? 'flex-grow-1' : 'gap-3'}
        mapClassName="severe-weather-alert-map-pane"
        panelClassName="severe-weather-alert-information-pane"
        mapContent={
          <SevereWeatherAlertMapContent
            areas={alertAreas.allAlertAreas}
            selectedArea={selectedArea}
            fallbackCenter={locations.effectiveLocation?.coords}
            onSelectArea={selectArea}
            scrollZoom={isDesktop}
          />
        }
        panelContent={
          <SevereWeatherAlertPanel
            areas={alertAreas.allAlertAreas}
            selectedArea={selectedArea}
            selectedAlert={selectedAlert}
            selectedAlertId={selectedAlertId}
            onSelectArea={selectAreaById}
            onSelectAlert={selectAlert}
          />
        }
      />

      {isFetching && !isLoading && (
        <WeatherMapUpdatingStatus message="Updating severe weather alerts" />
      )}
    </section>
  );
};

export default SevereWeatherAlerts;
